import { useCallback, useEffect, useState } from "react";
import { Download, FileText, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, downloadFile, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StaffLayout } from "@/components/StaffLayout";
import { GoldProgress, StatusPill } from "@/components/StatusPill";
import { CaseDrawer } from "@/components/CaseDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const COLUMNS = [
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In Progress" },
  { key: "review", label: "Review" },
  { key: "closed", label: "Closed" },
];

export default function StaffDashboard() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [query, setQuery] = useState("");
  const [openCaseId, setOpenCaseId] = useState(null);
  const [investigators, setInvestigators] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", client_name: "", client_email: "", category: "Corporate Investigation", description: "" });

  const load = useCallback(() => {
    api.get("/cases", { params: { q: query || undefined } }).then(({ data }) => setCases(data.cases));
  }, [query]);

  useEffect(load, [load]);
  useEffect(() => {
    api.get("/users").then(({ data }) => setInvestigators(data.filter((u) => u.role !== "client")));
  }, []);

  const move = async (caseId, status) => {
    const target = cases.find((c) => c.id === caseId);
    if (!target || target.status === status) return;
    setCases((prev) => prev.map((c) => (c.id === caseId ? { ...c, status } : c)));
    try {
      await api.patch(`/cases/${caseId}`, { status });
      toast.success(`${target.reference} moved to ${status.replace(/_/g, " ")}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      load();
    }
  };

  const createCase = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/cases", form);
      toast.success(`Case ${data.reference} created`);
      setCreating(false);
      setForm({ title: "", client_name: "", client_email: "", category: "Corporate Investigation", description: "" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <StaffLayout>
      <div className="flex flex-wrap items-end gap-5">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-gold">Case pipeline</p>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl text-[#F3F4F6]">Welcome back, {user?.name?.split(" ")[0]}</h1>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
            <Input
              data-testid="case-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search reference, title or client"
              className="w-72 pl-9 bg-white/5 border-white/15"
            />
          </div>
          <Button
            data-testid="export-cases-csv-btn"
            variant="outline"
            className="rounded-full border-white/20 bg-transparent hover:bg-white/10"
            onClick={() => downloadFile("/reports/export", "vineford-cases.csv", { kind: "cases", format: "csv" })}
          >
            <Download className="h-4 w-4 mr-2 text-gold" /> CSV
          </Button>
          <Button
            data-testid="export-cases-pdf-btn"
            variant="outline"
            className="rounded-full border-white/20 bg-transparent hover:bg-white/10"
            onClick={() => downloadFile("/reports/export", "vineford-cases.pdf", { kind: "cases", format: "pdf" })}
          >
            <FileText className="h-4 w-4 mr-2 text-gold" /> PDF
          </Button>
          <Dialog open={creating} onOpenChange={setCreating}>
            <DialogTrigger asChild>
              <Button data-testid="new-case-btn" className="rounded-full bg-gold text-navy hover:bg-gold-dark">
                <Plus className="h-4 w-4 mr-2" /> New case
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-strong border-white/10 text-[#F3F4F6] sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Open a new case</DialogTitle>
              </DialogHeader>
              <form onSubmit={createCase} className="space-y-4" data-testid="new-case-form">
                <Input
                  data-testid="new-case-title-input"
                  placeholder="Case title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="bg-white/5 border-white/15"
                  required
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    data-testid="new-case-client-name-input"
                    placeholder="Client name"
                    value={form.client_name}
                    onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                    className="bg-white/5 border-white/15"
                    required
                  />
                  <Input
                    data-testid="new-case-client-email-input"
                    type="email"
                    placeholder="Client email"
                    value={form.client_email}
                    onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                    className="bg-white/5 border-white/15"
                    required
                  />
                </div>
                <Input
                  data-testid="new-case-category-input"
                  placeholder="Category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="bg-white/5 border-white/15"
                />
                <Textarea
                  data-testid="new-case-description-input"
                  placeholder="Instruction summary"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-white/5 border-white/15"
                />
                <Button data-testid="new-case-submit-btn" className="w-full rounded-full bg-gold text-navy hover:bg-gold-dark">
                  Create case
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-4" data-testid="kanban-board">
        {COLUMNS.map((col) => {
          const items = cases.filter((c) => c.status === col.key);
          return (
            <section
              key={col.key}
              data-testid={`kanban-column-${col.key}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => move(e.dataTransfer.getData("case-id"), col.key)}
              className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 min-h-[320px]"
            >
              <div className="flex items-center justify-between px-1 pb-4">
                <h2 className="font-display text-lg text-[#F3F4F6]">{col.label}</h2>
                <span className="rounded-full bg-gold/15 px-2 py-0.5 text-xs text-gold">{items.length}</span>
              </div>
              <div className="space-y-3">
                {items.map((c) => (
                  <article
                    key={c.id}
                    data-testid="kanban-card"
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("case-id", c.id)}
                    onClick={() => setOpenCaseId(c.id)}
                    className="glass cursor-pointer rounded-xl p-4 hover:-translate-y-0.5 hover:border-white/30 transition-transform"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-medium tracking-wider text-gold">{c.reference}</span>
                      <StatusPill value={c.priority} />
                    </div>
                    <p className="mt-2 text-sm leading-snug text-[#F3F4F6]">{c.title}</p>
                    <p className="mt-2 text-xs text-[#6B7280]">{c.client_name}</p>
                    <GoldProgress value={c.progress} className="mt-3" />
                    <p className="mt-3 text-[11px] text-[#6B7280]">
                      {c.investigator_name || "Unassigned"} · {shortDate(c.updated_at)}
                    </p>
                  </article>
                ))}
                {!items.length ? <p className="px-1 text-xs text-[#6B7280]">Nothing here.</p> : null}
              </div>
            </section>
          );
        })}
      </div>

      <CaseDrawer
        caseId={openCaseId}
        onClose={() => setOpenCaseId(null)}
        onChanged={load}
        investigators={investigators}
        role={user?.role}
      />
    </StaffLayout>
  );
}
